import { useEffect, useState } from 'react';
import type {
  BillingApiKey,
  BillingApiKeyCreated,
  BillingCheckoutResult,
  BillingInvoice,
  BillingOrder,
  BillingOrderStatus,
  BillingPaymentTicket,
  BillingProduct,
  BillingProviderInfo,
  BillingStatements,
  BillingSummary,
} from '@shared/types';
import { canPurchaseAudience, canRequestInvoice } from '@shared/capabilities';
import { formatDateTime, formatMoney } from '@shared/format';
import { useAuthStore } from '../app/authStore';
import { api, ApiError, describeError } from '../lib/api';
import { detectClientType } from '../lib/clientType';
import { useAsync } from '../lib/useAsync';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CopyField,
  EmptyState,
  PageHeader,
  Skeleton,
  StatBlock,
} from '../components/ui';
import { Checkbox, Field, Input, Notice, Select } from '../components/form';
import { Modal, useToast } from '../components/feedback';
import { Table, Pagination, type Column } from '../components/table';
import { Icon } from '../components/Icon';

/**
 * 订阅与用量。
 *
 * 计费模型是「订单 + 累计消费 + 权益」：**没有余额，也不做预存**，每下一单买到的
 * 要么是一次服务、要么是一段有效期。金额一律由计费服务实时给出，所以页面上不会
 * 出现写死的数字 —— 这也是合规断言允许这一页展示金额的前提。
 *
 * 这一页不直接调支付 Worker：它只调控制台自己的 `/api/billing/*`，由 Worker 用
 * 服务令牌转发，浏览器里没有任何令牌。
 */

interface ProductListResponse {
  products: BillingProduct[];
}

interface OrderListResponse {
  items: BillingOrder[];
  page: number;
  pageSize: number;
  total: number;
}

interface ApiKeyListResponse {
  apiKeys: BillingApiKey[];
}

interface ProviderListResponse {
  mode: 'simulate' | 'live';
  /** 全站下单开关；旧版计费服务不返回时视为开启 */
  orderEnabled?: boolean;
  providers: BillingProviderInfo[];
}

interface InvoiceListResponse {
  items: BillingInvoice[];
  total: number;
  page: number;
  pageSize: number;
}

/** 「订单已创建」与「查看付款」共用同一个视图模型。 */
interface TicketView {
  order: BillingOrder;
  pay: BillingPaymentTicket | null;
}

const ORDER_STATUS_META: Record<
  BillingOrderStatus,
  { label: string; tone: 'neutral' | 'ok' | 'danger' | 'warn' | 'info' }
> = {
  pending: { label: '待支付', tone: 'warn' },
  paid: { label: '已支付', tone: 'ok' },
  failed: { label: '已失败', tone: 'danger' },
  refunded: { label: '已退款', tone: 'info' },
  closed: { label: '已关闭', tone: 'neutral' },
};

const KIND_LABEL: Record<BillingProduct['kind'], string> = {
  service: '单次服务',
  package: '资源包',
  subscription: '按期服务',
};

/** API Key 的权限标签：与权限矩阵里的说法保持一致，避免两处各叫一套。 */
const API_KEY_PERMISSION_LABEL: Record<string, string> = {
  'billing.manage': '下单',
  'billing.view': '查询明细',
};

/** 幂等键：同一次下单意图重复提交时，计费侧只会建一张单。 */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `key-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function BillingPage() {
  const can = useAuthStore((state) => state.can);
  const session = useAuthStore((state) => state.session);
  const toast = useToast();
  const canOrder = can('billing.manage');
  const canInvoice =
    session !== null && canRequestInvoice(session.tenant, session.user, session.role);
  /** 对公产品只对已通过审核的企业组织开放，避免个人空间升级后绕过资质审核。 */
  const canBuyProduct = (product: BillingProduct): boolean =>
    session !== null && canPurchaseAudience(session.tenant, session.user, product.audience);

  const [version, setVersion] = useState(0);
  const [page, setPage] = useState(1);
  // 一次下单意图配一个幂等键；确认框关掉就作废，避免误重放上一次的下单。
  const [intent, setIntent] = useState<{ product: BillingProduct; key: string } | null>(null);
  const [provider, setProvider] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  // 支付素材视图：下单后显示，之后也能从「我的订单」里随时再打开（否则关掉弹层就再也付不了款）。
  const [ticket, setTicket] = useState<TicketView | null>(null);
  const [ticketLoadingId, setTicketLoadingId] = useState<string | null>(null);
  const [closing, setClosing] = useState<BillingOrder | null>(null);

  // API 密钥：外部 AI / 服务用它直连计费接口，因此明文只在生成时出现一次。
  const [keyFormOpen, setKeyFormOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [keyIncludeView, setKeyIncludeView] = useState(false);
  const [keySubmitting, setKeySubmitting] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [revokingKey, setRevokingKey] = useState<BillingApiKey | null>(null);

  // 费用明细与发票
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceTitle, setInvoiceTitle] = useState('');
  const [invoiceTaxNo, setInvoiceTaxNo] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [invoiceFrom, setInvoiceFrom] = useState('');
  const [invoiceTo, setInvoiceTo] = useState('');
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceFieldErrors, setInvoiceFieldErrors] = useState<Record<string, string>>({});

  const products = useAsync<ProductListResponse>(() => api.get('/api/billing/products'), [version]);
  const providers = useAsync<ProviderListResponse>(() => api.get('/api/billing/providers'), [version]);
  const summary = useAsync<BillingSummary>(() => api.get('/api/billing/summary'), [version]);
  const orders = useAsync<OrderListResponse>(
    () => api.get(`/api/billing/orders?page=${page}&pageSize=20`),
    [page, version],
  );
  const apiKeys = useAsync<ApiKeyListResponse>(() => api.get('/api/billing/api-keys'), [version]);
  const statements = useAsync<BillingStatements>(() => api.get('/api/billing/statements'), [version]);
  const invoices = useAsync<InvoiceListResponse>(() => api.get('/api/billing/invoices'), [version]);

  function refresh(): void {
    setVersion((value) => value + 1);
  }

  /**
   * 从支付宝收银台切回来时刷新一次。
   *
   * 典型场景：用户在别的标签页付完款（或误关了支付窗口）回到这里，
   * 不刷新就会一直看到「待支付」。页面本来就只在可见时请求，开销很小。
   */
  useEffect(() => {
    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') refresh();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  async function confirmOrder(): Promise<void> {
    if (!intent) return;
    setSubmitting(true);
    try {
      const result = await api.post<BillingCheckoutResult>('/api/billing/orders', {
        productCode: intent.product.code,
        idempotencyKey: intent.key,
        // 必须显式告诉后端终端类型：支付宝的电脑网站支付与手机网站支付是两个产品，
        // 不传就一律按 pc 处理，手机用户会被送到电脑收银台。
        clientType: detectClientType(),
        ...(provider === '' ? {} : { provider }),
      });
      setIntent(null);
      setTicket(result);
      refresh();
    } catch (error) {
      toast.push(describeError(error), 'danger');
    } finally {
      setSubmitting(false);
    }
  }

  /** 模拟通道的「付款」：仅本地联调，真实通道会被支付侧拒绝。 */
  async function simulatePay(orderId: string): Promise<void> {
    try {
      await api.post(`/api/billing/orders/${orderId}/simulate-pay`);
      toast.push('已模拟支付成功', 'ok');
      setTicket(null);
      refresh();
    } catch (error) {
      toast.push(describeError(error), 'danger');
    }
  }

  async function submitInvoice(): Promise<void> {
    setInvoiceBusy(true);
    setInvoiceError(null);
    setInvoiceFieldErrors({});
    try {
      await api.post('/api/billing/invoices', {
        title: invoiceTitle.trim(),
        taxNo: invoiceTaxNo.trim() || undefined,
        email: invoiceEmail.trim(),
        periodFrom: invoiceFrom,
        periodTo: invoiceTo,
      });
      setInvoiceOpen(false);
      setInvoiceTitle('');
      setInvoiceTaxNo('');
      setInvoiceFrom('');
      setInvoiceTo('');
      toast.push('开票申请已提交，我们会人工开具', 'ok');
      refresh();
    } catch (error) {
      if (error instanceof ApiError) {
        setInvoiceFieldErrors(error.fieldErrors);
        setInvoiceError(error.message);
      } else {
        setInvoiceError(describeError(error));
      }
    } finally {
      setInvoiceBusy(false);
    }
  }

  async function confirmClose(): Promise<void> {
    if (!closing) return;
    try {
      await api.post(`/api/billing/orders/${closing.id}/close`);
      toast.push('订单已关闭', 'ok');
      setClosing(null);
      refresh();
    } catch (error) {
      toast.push(describeError(error), 'danger');
    }
  }

  /** 重新取回一张待支付订单的支付素材并打开弹层。 */
  async function openTicket(order: BillingOrder): Promise<void> {
    setTicketLoadingId(order.id);
    try {
      const view = await api.get<TicketView>(
        `/api/billing/orders/${order.id}/ticket?clientType=${detectClientType()}`,
      );
      setTicket(view);
    } catch (error) {
      toast.push(describeError(error), 'danger');
    } finally {
      setTicketLoadingId(null);
    }
  }

  async function createKey(): Promise<void> {
    const name = keyName.trim();
    if (name === '') {
      toast.push('请填写密钥用途名称', 'danger');
      return;
    }
    setKeySubmitting(true);
    try {
      const created = await api.post<BillingApiKeyCreated>('/api/billing/api-keys', {
        name,
        includeView: keyIncludeView,
      });
      setKeyFormOpen(false);
      setKeyName('');
      setKeyIncludeView(false);
      setCreatedSecret(created.secret);
      refresh();
    } catch (error) {
      toast.push(describeError(error), 'danger');
    } finally {
      setKeySubmitting(false);
    }
  }

  async function confirmRevoke(): Promise<void> {
    if (!revokingKey) return;
    try {
      await api.post(`/api/billing/api-keys/${revokingKey.id}/revoke`);
      toast.push('密钥已吊销', 'ok');
      setRevokingKey(null);
      refresh();
    } catch (error) {
      toast.push(describeError(error), 'danger');
    }
  }

  const productColumns: Column<BillingProduct>[] = [
    {
      key: 'name',
      header: '服务',
      primary: true,
      render: (row) => (
        <div>
          <p className="font-medium text-fg">{row.name}</p>
          <p className="mt-0.5 font-mono text-xs text-fg-subtle">{row.code}</p>
        </div>
      ),
    },
    {
      key: 'kind',
      header: '类型',
      render: (row) => <Badge tone="brand">{KIND_LABEL[row.kind]}</Badge>,
    },
    {
      key: 'term',
      header: '有效期',
      render: (row) => (
        <span className="text-fg-muted">{row.termDays === null ? '一次性' : `${row.termDays} 天`}</span>
      ),
    },
    {
      key: 'amount',
      header: '金额',
      align: 'right',
      render: (row) => (
        <span className="tabular font-medium text-fg">
          {formatMoney(row.priceMinor, row.currency)}
        </span>
      ),
    },
    {
      key: 'availability',
      header: '状态',
      align: 'right',
      render: (row) => {
        if (orderingClosed) return <Badge tone="warn">上线中 敬请期待</Badge>;
        if (row.salesPaused) return <Badge tone="warn">暂停接单</Badge>;
        if (!canBuyProduct(row)) return <Badge tone="warn">企业认证后可用</Badge>;
        return <span className="text-xs text-fg-subtle">可下单</span>;
      },
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      // 下单是这一页唯一的写操作，移动端必须能给到入口 —— 不能隐藏。
      render: (row) => {
        const audienceBlocked = !canBuyProduct(row);
        const blocked = orderingClosed || row.salesPaused === true || audienceBlocked;
        return (
          <Button
            size="sm"
            variant="primary"
            icon="plus"
            disabled={!canOrder || blocked}
            title={
              orderingClosed
                ? '平台正在升级服务，暂时无法下单'
                : row.salesPaused
                  ? '该服务暂停接单'
                  : audienceBlocked
                    ? '该服务为对公产品，仅向已通过企业资质审核的企业组织开放'
                    : canOrder
                      ? undefined
                      : '只有组织所有者可以下单'
            }
            onClick={() => setIntent({ product: row, key: newIdempotencyKey() })}
          >
            {audienceBlocked ? '企业专享' : blocked ? '敬请期待' : '下单'}
          </Button>
        );
      },
    },
  ];

  const orderColumns: Column<BillingOrder>[] = [
    {
      key: 'subject',
      header: '项目',
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-fg">{row.subject ?? row.productCode ?? '实时订单'}</p>
          <p className="mt-0.5 font-mono text-xs break-all text-fg-subtle">{row.id}</p>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: '时间',
      render: (row) => (
        <span className="tabular text-fg-muted">{formatDateTime(row.createdAt)}</span>
      ),
    },
    {
      key: 'amount',
      header: '金额',
      align: 'right',
      render: (row) => (
        <span className="tabular text-fg">{formatMoney(row.amountMinor, row.currency)}</span>
      ),
    },
    {
      key: 'status',
      header: '状态',
      render: (row) => {
        const meta = ORDER_STATUS_META[row.status];
        return <Badge tone={meta.tone}>{meta.label}</Badge>;
      },
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      // 订单管理依赖这两个入口：误关支付窗口后要能重新打开，待支付时还要能取消。
      // 之前带 hideOnMobile，移动端等于完全没有订单管理能力。
      render: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            loading={ticketLoadingId === row.id}
            onClick={() => void openTicket(row)}
          >
            订单详情
          </Button>
          {row.status === 'pending' && canOrder ? (
            <Button size="sm" onClick={() => setClosing(row)}>
              取消订单
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const apiKeyColumns: Column<BillingApiKey>[] = [
    {
      key: 'name',
      header: '用途',
      primary: true,
      render: (row) => (
        <div>
          <p className="font-medium text-fg">{row.name}</p>
          <p className="mt-0.5 font-mono text-xs text-fg-subtle">{row.prefix}…</p>
        </div>
      ),
    },
    {
      key: 'permissions',
      header: '权限',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.permissions.length === 0 ? (
            <span className="text-xs text-fg-muted">—</span>
          ) : (
            row.permissions.map((permission) => (
              <Badge key={permission} tone={permission === 'billing.manage' ? 'brand' : 'neutral'}>
                {API_KEY_PERMISSION_LABEL[permission] ?? permission}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: '状态',
      render: (row) =>
        row.revokedAt === null ? <Badge tone="ok">有效</Badge> : <Badge tone="neutral">已吊销</Badge>,
    },
    {
      key: 'lastUsedAt',
      header: '最近使用',
      hideOnMobile: true,
      render: (row) => (
        <span className="text-fg-muted">
          {row.lastUsedAt === null ? '尚未使用' : formatDateTime(row.lastUsedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      hideOnMobile: true,
      render: (row) =>
        row.revokedAt === null && canOrder ? (
          <Button size="sm" variant="danger" onClick={() => setRevokingKey(row)}>
            吊销
          </Button>
        ) : null,
    },
  ];

  const entitlements = summary.data?.entitlements ?? [];
  const allProviders = providers.data?.providers ?? [];
  const enabledProviders = allProviders.filter((item) => item.enabled);
  /** 全站下单开关：关闭时所有商品都不可下单，展示为「上线中 敬请期待」。 */
  const orderingClosed = providers.data?.orderEnabled === false;

  return (
    <div className="space-y-5">
      <PageHeader
        title="订阅与用量"
        description="按订单结算：每下一单买到的要么是一次服务，要么是一段有效期。没有余额，也不做预存。"
        action={
          <Button icon="refresh" onClick={refresh}>
            刷新
          </Button>
        }
      />

      <Card>
        <CardHeader
          title="概览"
          hint="累计消费＝已付款订单之和，由计费服务从订单重算，因此重复结算不会重复计数"
        />
        {summary.error ? (
          <Notice tone="danger">{describeError(summary.error)}</Notice>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <StatBlock
                label="累计消费"
                value={formatMoney(summary.data?.totalSpentMinor, summary.data?.currency)}
              />
              <StatBlock label="已支付订单" value={summary.data?.paidOrderCount ?? '—'} />
              <StatBlock label="最近付款" value={formatDateTime(summary.data?.lastPaidAt)} />
            </div>

            <div className="mt-5">
              <p className="mb-2 text-[13px] font-medium text-fg">当前有效权益</p>
              {entitlements.length === 0 ? (
                <p className="text-[13px] text-fg-muted">
                  还没有生效中的权益。一次性服务不产生长期权益，付款后在订单里留痕。
                </p>
              ) : (
                <ul className="space-y-2">
                  {entitlements.map((item) => (
                    <li
                      key={item.code}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-control bg-nest-2 px-4 py-3 text-[13px]"
                    >
                      <span className="font-mono text-fg">{item.code}</span>
                      <span className="text-fg-muted">
                        {item.expiresAt === null
                          ? '长期有效'
                          : `到期 ${formatDateTime(item.expiresAt)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </Card>

      <Card>
        <CardHeader
          title="可下单的服务"
          hint="金额由计费服务实时给出；下单时以计费服务目录为准，前端无法指定金额"
        />
        {!canOrder ? (
          <div className="mb-4">
            <Notice tone="info">
              当前角色可以查看服务与订单；下单与关闭订单收在组织所有者手里。
            </Notice>
          </div>
        ) : null}
        {products.error ? (
          <Notice tone="danger">{describeError(products.error)}</Notice>
        ) : (
          <Table
            caption="可下单的服务"
            columns={productColumns}
            rows={products.data?.products ?? []}
            getKey={(row) => row.id}
            loading={products.loading}
            empty={{ title: '暂无可下单的服务', description: '计费服务里还没有上架的服务。' }}
          />
        )}
      </Card>

      <Card>
        <CardHeader title="我的订单" hint="订单是计费服务里的副本，出现差异时以计费服务为准" />
        {orders.error ? (
          <Notice tone="danger">{describeError(orders.error)}</Notice>
        ) : (
          <>
            <Table
              caption="我的订单"
              columns={orderColumns}
              rows={orders.data?.items ?? []}
              getKey={(row) => row.id}
              loading={orders.loading}
              empty={{ title: '还没有订单', description: '下单后这里会出现待支付的订单。' }}
            />
            <Pagination
              page={page}
              pageSize={orders.data?.pageSize ?? 20}
              total={orders.data?.total ?? 0}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      <Card>
        <CardHeader
          title="费用明细"
          hint="按月汇总已收金额与退款；净额 = 已收 − 已退"
        />
        {statements.loading ? (
          <Skeleton className="h-24 w-full" />
        ) : statements.error ? (
          <Notice tone="danger">{describeError(statements.error)}</Notice>
        ) : (statements.data?.statements.length ?? 0) === 0 ? (
          <EmptyState
            icon="apps"
            title="还没有可统计的费用"
            description="下单并完成支付后，这里会按月汇总。"
          />
        ) : (
          <ul className="divide-y divide-line">
            {(statements.data?.statements ?? []).map((statement) => (
              <li key={statement.month} className="py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-[13px] font-medium text-fg">{statement.month}</p>
                  <p className="tabular text-[13px] text-fg">
                    {formatMoney(statement.amountMinor, statement.currency)}
                    <span className="ml-2 text-xs text-fg-muted">
                      退款 {formatMoney(statement.refundedMinor, statement.currency)} · 净额{' '}
                      {formatMoney(statement.amountMinor - statement.refundedMinor, statement.currency)}
                    </span>
                  </p>
                </div>
                {statement.items.length > 0 ? (
                  <ul className="mt-1 space-y-0.5 text-xs text-fg-muted">
                    {statement.items.map((item) => (
                      <li key={`${statement.month}-${item.productCode ?? item.productName ?? 'x'}`}>
                        {item.productName ?? item.productCode ?? '未命名'} · {item.orderCount} 单 ·{' '}
                        {formatMoney(item.amountMinor, statement.currency)}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="发票"
          hint={
            canInvoice
              ? '按时间段申请，金额由计费服务按已支付订单重算'
              : '申请发票需要：所有者角色 + 邮箱已验证 + 企业资质已通过'
          }
          action={
            <Button
              variant="primary"
              icon="plus"
              disabled={!canInvoice}
              title={canInvoice ? undefined : '当前不满足开票条件'}
              onClick={() => {
                setInvoiceError(null);
                setInvoiceEmail(session?.user.email ?? '');
                setInvoiceOpen(true);
              }}
            >
              申请发票
            </Button>
          }
        />
        {!canInvoice ? (
          <div className="mb-4">
            <Notice tone="info">
              开票是金额动作，只开放给组织所有者，且需要邮箱已验证、企业资质已通过审核。
            </Notice>
          </div>
        ) : null}
        {invoices.error ? (
          <Notice tone="danger">{describeError(invoices.error)}</Notice>
        ) : (
          <Table
            caption="发票申请"
            columns={[
              {
                key: 'title',
                header: '抬头',
                render: (row) => (
                  <div>
                    <p className="text-fg">{row.title}</p>
                    <p className="text-xs text-fg-subtle">{row.email}</p>
                  </div>
                ),
              },
              {
                key: 'amount',
                header: '金额',
                render: (row) => (
                  <span className="tabular">{formatMoney(row.amountMinor, row.currency)}</span>
                ),
              },
              {
                key: 'period',
                header: '时间段',
                render: (row) => (
                  <span className="text-xs text-fg-muted">
                    {row.periodFrom} ~ {row.periodTo}
                  </span>
                ),
              },
              {
                key: 'status',
                header: '状态',
                render: (row) => (
                  <Badge
                    tone={
                      row.status === 'issued' ? 'ok' : row.status === 'rejected' ? 'danger' : 'warn'
                    }
                  >
                    {row.status === 'issued' ? '已开具' : row.status === 'rejected' ? '已驳回' : '待开具'}
                  </Badge>
                ),
              },
              {
                key: 'created',
                header: '申请时间',
                render: (row) => (
                  <span className="text-xs text-fg-muted">{formatDateTime(row.createdAt)}</span>
                ),
              },
            ]}
            rows={invoices.data?.items ?? []}
            getKey={(row) => row.id}
            loading={invoices.loading}
            empty={{ title: '还没有发票申请', description: '需要报销时在这里申请。' }}
          />
        )}
      </Card>

      <Card>
        <CardHeader
          title="API 密钥"
          hint="外部 AI / 服务带上密钥调用计费接口即可下单，不必再模拟登录；明文只在生成时出现一次，之后无法找回"
          action={
            <Button
              variant="primary"
              icon="plus"
              disabled={!canOrder}
              title={canOrder ? undefined : '只有组织所有者可以生成密钥'}
              onClick={() => setKeyFormOpen(true)}
            >
              生成密钥
            </Button>
          }
        />
        {!canOrder ? (
          <div className="mb-4">
            <Notice tone="info">
              当前角色可以查看密钥前缀；生成与吊销收在组织所有者手里。
            </Notice>
          </div>
        ) : null}
        {apiKeys.error ? (
          <Notice tone="danger">{describeError(apiKeys.error)}</Notice>
        ) : (
          <Table
            caption="API 密钥"
            columns={apiKeyColumns}
            rows={apiKeys.data?.apiKeys ?? []}
            getKey={(row) => row.id}
            loading={apiKeys.loading}
            empty={{
              title: '还没有 API 密钥',
              description: '生成一串密钥交给外部 AI / 服务，它就能直接调用计费接口。',
            }}
          />
        )}
      </Card>

      <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <Icon name="help" className="h-3.5 w-3.5" />
        我们不销售无人机整机，因此这里也不会出现整机相关的费用项。
      </p>

      <Modal
        open={intent !== null}
        title="确认下单"
        description="下单只会创建一张待支付的订单，不会直接扣款。"
        onClose={() => setIntent(null)}
        footer={
          <>
            <Button onClick={() => setIntent(null)}>取消</Button>
            <Button variant="primary" loading={submitting} onClick={() => void confirmOrder()}>
              确认下单
            </Button>
          </>
        }
      >
        {intent ? (
          <dl className="space-y-2 text-[13px]">
            <div className="flex justify-between gap-4">
              <dt className="text-fg-muted">服务</dt>
              <dd className="font-medium text-fg">{intent.product.name}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-fg-muted">金额</dt>
              <dd className="tabular font-medium text-fg">
                {formatMoney(intent.product.priceMinor, intent.product.currency)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-fg-muted">有效期</dt>
              <dd className="text-fg">
                {intent.product.termDays === null ? '一次性' : `${intent.product.termDays} 天`}
              </dd>
            </div>

            {allProviders.length > 1 ? (
              <div className="flex items-start justify-between gap-4">
                <dt className="text-fg-muted">支付方式</dt>
                <dd className="w-56">
                  <Select
                    value={provider === '' ? enabledProviders[0]?.code ?? '' : provider}
                    onChange={setProvider}
                    disabled={enabledProviders.length === 0}
                    options={allProviders.map((item) => ({
                      value: item.code,
                      label: item.enabled
                        ? item.label
                        : `${item.label}（${item.flagEnabled === false ? '已停用' : '未配置'}）`,
                    }))}
                  />
                  {enabledProviders.length === 0 ? (
                    <p className="mt-1 text-xs text-fg-subtle">支付通道维护中，请稍后再试。</p>
                  ) : null}
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </Modal>

      <Modal
        open={ticket !== null}
        title="订单详情"
        description="订单在到期前有效；随时可以从「我的订单」里重新打开这张账单。支付结果以支付宝的异步通知为准，入账后这里会自动更新。"
        onClose={() => setTicket(null)}
        footer={<Button onClick={() => setTicket(null)}>知道了</Button>}
      >
        {ticket ? (
          <div className="space-y-4">
            <dl className="space-y-2 text-[13px]">
              <div className="flex justify-between gap-4">
                <dt className="text-fg-muted">状态</dt>
                <dd>
                  <Badge tone={ORDER_STATUS_META[ticket.order.status].tone}>
                    {ORDER_STATUS_META[ticket.order.status].label}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-fg-muted">待付金额</dt>
                <dd className="tabular font-medium text-fg">
                  {formatMoney(ticket.order.amountMinor, ticket.order.currency)}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-fg-muted">账单到期</dt>
                <dd className="text-fg">{formatDateTime(ticket.pay?.expiresAt)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-fg-muted">订单号</dt>
                <dd className="min-w-0 text-right font-mono text-xs break-all text-fg-subtle">
                  {ticket.order.id}
                </dd>
              </div>
            </dl>

            {ticket.pay === null ? (
              <Notice tone="info">
                {ticket.order.status === 'paid'
                  ? '这张订单已完成支付，没有待付账单。'
                  : ticket.order.status === 'closed'
                    ? '这张订单已取消，不能再支付。需要的话请重新下单。'
                    : ticket.order.status === 'refunded'
                      ? '这张订单已全额退款，没有待付账单。'
                      : '这张订单当前没有可支付的账单（可能已支付、已关闭，或不需要立刻支付）。'}
              </Notice>
            ) : (
              <>
                {ticket.pay.redirectUrl ? (
                  <div className="space-y-2">
                    <a href={ticket.pay.redirectUrl} target="_blank" rel="noreferrer">
                      <Button variant="primary" block icon="external">
                        前往支付
                      </Button>
                    </a>
                    <p className="text-center text-xs text-fg-muted">
                      {ticket.pay.note ?? '在新窗口完成付款后回到本页，订单状态会自动更新。'}
                    </p>
                  </div>
                ) : null}

                {ticket.pay.qrCodeUrl !== null && ticket.pay.qrCodeSvg !== null ? (
                  <div className="flex flex-col items-center gap-2 rounded-control bg-nest-2 px-4 py-4">
                    <img
                      alt="收款二维码"
                      className="h-44 w-44"
                      src={`data:image/svg+xml;utf8,${encodeURIComponent(ticket.pay.qrCodeSvg)}`}
                    />
                    <p className="text-xs text-fg-muted">
                      {ticket.pay.provider === 'wechatpay' ? '请使用微信扫码支付' : '用支付宝扫一扫完成付款'}
                    </p>
                  </div>
                ) : null}

                {!ticket.pay.redirectUrl &&
                (ticket.pay.qrCodeUrl === null || ticket.pay.qrCodeSvg === null) ? (
                  <Notice tone="warn">
                    {ticket.pay.note ??
                      '当前收款通道还不能在控制台内生成收款链接，请把下面的账单交给支付宝完成付款。'}
                  </Notice>
                ) : null}
              </>
            )}

            {ticket.pay?.simulated ? (
              <div className="space-y-2">
                <Notice tone="warn">
                  这张账单来自模拟通道，不会真实扣款。用于本地联调时可点下面的按钮直接标记为已支付。
                </Notice>
                <Button variant="secondary" block onClick={() => void simulatePay(ticket.order.id)}>
                  模拟支付成功
                </Button>
              </div>
            ) : null}

            {ticket.pay ? (
              <CopyField
                label="Payment-Needed 账单"
                value={ticket.pay.bill}
                hint="买家侧的支付宝 AI付 读取这段账单即可完成支付"
              />
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={closing !== null}
        title="取消订单"
        description="取消后这张订单不能再支付，需要重新下单。"
        onClose={() => setClosing(null)}
        footer={
          <>
            <Button onClick={() => setClosing(null)}>返回</Button>
            <Button variant="danger" onClick={() => void confirmClose()}>
              确认取消
            </Button>
          </>
        }
      >
        {closing ? (
          <p className="text-[13px] text-fg-muted">
            订单 {closing.id}（{formatMoney(closing.amountMinor, closing.currency)}）将被取消。
          </p>
        ) : null}
      </Modal>

      <Modal
        open={keyFormOpen}
        title="生成 API 密钥"
        description="密钥供外部 AI / 服务调用计费接口使用；生成后只显示一次。"
        onClose={() => setKeyFormOpen(false)}
        footer={
          <>
            <Button onClick={() => setKeyFormOpen(false)}>取消</Button>
            <Button variant="primary" loading={keySubmitting} onClick={() => void createKey()}>
              生成
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="用途名称" hint="只用于你自己识别这串密钥，例如「机队分析智能体」">
            <Input
              value={keyName}
              onChange={setKeyName}
              placeholder="例如：机队分析智能体"
              maxLength={60}
              autoFocus
            />
          </Field>
          <Checkbox
            checked={keyIncludeView}
            onChange={setKeyIncludeView}
            label="同时允许查询订单与消费明细"
          />
          <Notice tone="info">
            密钥默认只能下单；勾选后会额外获得查看订单与消费明细的权限。两者都不含改付款方式、退款等管理能力。
          </Notice>
        </div>
      </Modal>

      <Modal
        open={invoiceOpen}
        title="申请发票"
        description="金额由计费服务按所选时间段内的已支付订单重算，提交后由财务人工开具。"
        onClose={() => setInvoiceOpen(false)}
        footer={
          <>
            <Button onClick={() => setInvoiceOpen(false)}>取消</Button>
            <Button variant="primary" loading={invoiceBusy} onClick={() => void submitInvoice()}>
              提交申请
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {invoiceError ? <Notice tone="danger">{invoiceError}</Notice> : null}
          <Field label="发票抬头" htmlFor="invoice-title" error={invoiceFieldErrors.title} required>
            <Input
              id="invoice-title"
              value={invoiceTitle}
              onChange={setInvoiceTitle}
              placeholder="公司全称"
              maxLength={120}
            />
          </Field>
          <Field label="税号" htmlFor="invoice-tax" error={invoiceFieldErrors.taxNo}>
            <Input id="invoice-tax" value={invoiceTaxNo} onChange={setInvoiceTaxNo} maxLength={40} />
          </Field>
          <Field label="接收邮箱" htmlFor="invoice-email" error={invoiceFieldErrors.email} required>
            <Input
              id="invoice-email"
              type="email"
              value={invoiceEmail}
              onChange={setInvoiceEmail}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="开始日期" htmlFor="invoice-from" error={invoiceFieldErrors.periodFrom} required>
              <Input id="invoice-from" type="text" value={invoiceFrom} onChange={setInvoiceFrom} placeholder="YYYY-MM-DD" />
            </Field>
            <Field label="结束日期" htmlFor="invoice-to" error={invoiceFieldErrors.periodTo} required>
              <Input id="invoice-to" type="text" value={invoiceTo} onChange={setInvoiceTo} placeholder="YYYY-MM-DD" />
            </Field>
          </div>
          <Notice tone="info">
            只统计「已支付且未退款」的订单。若该时间段没有可开票订单，提交会被拒绝。
          </Notice>
        </div>
      </Modal>

      <Modal
        open={createdSecret !== null}
        title="密钥已生成"
        description="明文只显示这一次，关闭后无法再查看。请立即复制并妥善保存。"
        onClose={() => setCreatedSecret(null)}
        footer={<Button onClick={() => setCreatedSecret(null)}>我已保存</Button>}
      >
        {createdSecret !== null ? (
          <div className="space-y-4">
            <CopyField
              label="API Key"
              value={createdSecret}
              hint="调用计费接口时放入 X-API-Key 请求头"
            />
            <Notice tone="warn">
              这串密钥等同于该组织的下单权限。泄露后请立即吊销并重新生成。
            </Notice>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={revokingKey !== null}
        title="吊销 API 密钥"
        description="吊销后这串密钥立即失效，且不可恢复。"
        onClose={() => setRevokingKey(null)}
        footer={
          <>
            <Button onClick={() => setRevokingKey(null)}>取消</Button>
            <Button variant="danger" onClick={() => void confirmRevoke()}>
              吊销
            </Button>
          </>
        }
      >
        {revokingKey !== null ? (
          <p className="text-[13px] text-fg-muted">
            密钥「{revokingKey.name}」（{revokingKey.prefix}…）将被吊销。
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
